import { CaretRight, MagnifyingGlass, Package, Plus } from '@/core/ui/icons';
import { useState } from 'react';
import { Badge, Button, EmptyState, ErrorState, Page, PageHeader, Skeleton, FilterMenu } from '@/core/ui';
import { useProducts } from '../application/queries';
import { BRANDS, brandLabel, STATUS_LABELS } from '../domain/product';

const fold = (s: string) => s.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase();

interface Props {
  canEdit: boolean;
  onOpen: (productId: string) => void;
  onCreate: () => void;
}

/** Catálogo comercial. Publicados aparecem no portal, em Produtos. */
export function ProductsPage({ canEdit, onOpen, onCreate }: Props) {
  const products = useProducts();
  const [brand, setBrand] = useState<string>('');
  const [term, setTerm] = useState('');

  const visible = (products.data ?? [])
    .filter((p) => !brand || p.brand === brand)
    .filter((p) => !term.trim() || fold(`${p.name} ${p.summary}`).includes(fold(term.trim())));

  return (
    <Page>
      <PageHeader
        title="Produtos"
        description="Os produtos exibidos no portal."
        actions={
          canEdit && (
            <Button onClick={onCreate}>
              <Plus aria-hidden className="size-4" />
              Novo produto
            </Button>
          )
        }
      />

      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
        <FilterMenu
          label="Marca"
          value={brand || 'todas'}
          options={[
            { value: 'todas', label: 'Todas' },
            ...BRANDS.map((b) => ({ value: b.id, label: b.label })),
          ]}
          onChange={(v) => setBrand(v === 'todas' ? '' : v)}
        />
        <div className="relative lg:ml-auto lg:w-80">
          <MagnifyingGlass
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-gray-400"
          />
          <input
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Buscar produto"
            aria-label="Buscar produto"
            className="h-10 w-full rounded-lg border border-line-strong bg-surface pr-3 pl-9 text-sm placeholder:text-gray-400 focus:ring-2 focus:ring-teal-300 focus:outline-none"
          />
        </div>
      </div>

      {products.isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : products.isError ? (
        <ErrorState error={products.error} onRetry={() => void products.refetch()} />
      ) : (products.data ?? []).length === 0 ? (
        <EmptyState
          title="Nenhum produto ainda"
          description={canEdit ? 'Cadastre o primeiro para ele aparecer no portal.' : undefined}
          action={canEdit && <Button onClick={onCreate}>Novo produto</Button>}
        />
      ) : visible.length === 0 ? (
        <EmptyState title="Nenhum produto encontrado" description="Tente outra marca ou outra busca." />
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-surface">
          {visible.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onOpen(p.id)}
                className="group flex w-full items-center gap-5 px-6 py-4 text-left transition-colors hover:bg-surface-2"
              >
                <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-surface-2 ring-1 ring-line">
                  {p.image_url ? (
                    <img src={p.image_url} alt="" loading="lazy" className="size-full object-cover" />
                  ) : (
                    <Package aria-hidden className="size-6 text-gray-300" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-medium text-fg">{p.name}</p>
                  <p className="mt-0.5 truncate text-sm text-fg-subtle">{brandLabel(p.brand)}</p>
                </div>
                {/* Publicado é o normal: só o que está fora do portal ganha selo. */}
                {p.status !== 'published' && (
                  <Badge tone="neutral">{STATUS_LABELS[p.status] ?? p.status}</Badge>
                )}
                <CaretRight
                  aria-hidden
                  className="size-4 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-gray-500"
                />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Page>
  );
}
